import { useEffect, useRef, useCallback } from 'react';
import { useMap } from '@vis.gl/react-google-maps';
import { useBikeLayer, usePreWarmBikeTiles } from '../../hooks/useBikeLayer';

/**
 * Schedule work during browser idle time so it never blocks scroll/zoom.
 * Falls back to setTimeout(0) on Safari which doesn't support requestIdleCallback.
 * Returns an ID that can be passed to cancelIdle().
 */
function scheduleIdle(cb, timeoutMs = 2000) {
  if ('requestIdleCallback' in window) {
    return requestIdleCallback(cb, { timeout: timeoutMs });
  }
  return setTimeout(cb, 0);
}

function cancelIdle(id) {
  if ('requestIdleCallback' in window) {
    cancelIdleCallback(id);
  } else {
    clearTimeout(id);
  }
}

/** Number of GeoJSON features added to the Data layer per idle slot. */
const CHUNK_SIZE = 50;

/**
 * Stroke color and weight per bikeability score.
 * excellent = dedicated cycleway     (green)
 * good      = protected lane / track (blue)
 * moderate  = painted bike lane      (amber)
 * low       = sharrow / minor accom. (gray)
 */
const BIKEABILITY_STYLES = {
  excellent: { color: '#16a34a', weight: 4 },
  good:      { color: '#2563eb', weight: 3 },
  moderate:  { color: '#d97706', weight: 2.5 },
  low:       { color: '#9ca3af', weight: 2 },
};

function styleFeature(feature) {
  const score = feature.getProperty('bikeability') ?? 'low';
  const { color, weight } = BIKEABILITY_STYLES[score] ?? BIKEABILITY_STYLES.low;
  return { strokeColor: color, strokeWeight: weight, strokeOpacity: 0.85, clickable: false };
}

/**
 * Renders OSM bike infrastructure as colored polylines on the map.
 *
 * Uses a separate google.maps.Data instance (never the map's default map.data)
 * to avoid conflicts with other layers.
 *
 * Performance: features are added INCREMENTALLY to the Data layer as each tile
 * arrives. The layer is never cleared and rebuilt — that pattern caused
 * noticeable map lag with large feature sets.
 *
 * @param {boolean}  enabled
 * @param {function} onLoadingProgress  (0–100 | null) forwarded from useBikeLayer
 */
export default function BikeLayer({ enabled, onLoadingProgress }) {
  const map          = useRef(useMap());
  const dataLayerRef = useRef(null);
  const renderedRef  = useRef(new Set()); // tile keys already committed to the Data layer
  const pendingRef   = useRef(new Map()); // key → cancel() — chunk sequence in progress but not yet fully rendered

  // Always silently pre-warm tiles for the visible area, regardless of toggle state
  usePreWarmBikeTiles();

  // Create the Data layer once when the map mounts; set the style function once.
  useEffect(() => {
    const m = map.current;
    if (!m) return undefined;
    const layer = new window.google.maps.Data({ map: m });
    layer.setStyle(styleFeature);
    dataLayerRef.current = layer;
    return () => {
      layer.setMap(null);
      dataLayerRef.current = null;
    };
  }, []); // deliberately empty — only runs once

  // When the layer is toggled off, cancel pending renders and clear the Data layer
  useEffect(() => {
    if (!enabled) {
      // Cancel all in-progress chunk sequences
      for (const cancel of pendingRef.current.values()) cancel();
      pendingRef.current.clear();
      renderedRef.current.clear();
      const layer = dataLayerRef.current;
      if (layer) layer.forEach((f) => layer.remove(f));
    }
  }, [enabled]);

  /**
   * Called by useBikeLayer for each tile that is ready.
   *
   * Features are split into CHUNK_SIZE batches, each scheduled as a separate
   * requestIdleCallback so the main thread is only ever blocked for one small
   * batch at a time.  Scroll/zoom events get CPU time between every chunk.
   *
   * pendingRef stores a cancel() function (not a raw ID) so we can cancel
   * the entire multi-step sequence when the layer is disabled mid-render.
   */
  const onTileReady = useCallback((key, geojson) => {
    // Skip if already rendered or already scheduled
    if (renderedRef.current.has(key) || pendingRef.current.has(key)) return;
    if (!dataLayerRef.current) return;

    const features = geojson.features ?? [];
    let index = 0;

    // ctrl is shared across all chunks for this tile
    const ctrl = { cancelled: false, idleId: null };

    const cancel = () => {
      ctrl.cancelled = true;
      if (ctrl.idleId !== null) {
        cancelIdle(ctrl.idleId);
        ctrl.idleId = null;
      }
    };

    // Store the cancel function so the disable effect can call it
    pendingRef.current.set(key, cancel);

    function addChunk() {
      ctrl.idleId = null;
      if (ctrl.cancelled || !dataLayerRef.current) return;

      const end = Math.min(index + CHUNK_SIZE, features.length);
      dataLayerRef.current.addGeoJson({
        type: 'FeatureCollection',
        features: features.slice(index, end),
      });
      index = end;

      if (index < features.length) {
        // Schedule the next chunk — 500ms timeout so tiles always finish
        // eventually even on a persistently busy page
        ctrl.idleId = scheduleIdle(addChunk, 500);
      } else {
        // All chunks committed
        pendingRef.current.delete(key);
        renderedRef.current.add(key);
      }
    }

    // First chunk gets a 2s timeout; subsequent chunks use 500ms
    ctrl.idleId = scheduleIdle(addChunk, 2000);
  }, []); // no deps — uses refs only

  useBikeLayer(enabled, onLoadingProgress, onTileReady);

  return null;
}
